<template>
  <div class="tag-group-component">
    <div
      class="tag-image-container"
      :class="{ readonly }"
      v-if="imageLoad"
      @click.stop="searchClick"
      ref="group"
      :style="containerStyle"
    >
      <template v-if="type === 'people'">
        <div v-for="(tag, index) in image.usertags" :key="tag.username">
          <tag-item
            :readonly="readonly"
            @remove="remove(index)"
            v-model="image.usertags[index]"
          ></tag-item>
        </div>
      </template>
      <template v-if="type === 'product'">
        <div v-for="(tag, index) in image.product_tags" :key="tag.id">
          <tag-item
            :type="type"
            :readonly="readonly"
            @remove="remove(index)"
            v-model="image.product_tags[index]"
          ></tag-item>
        </div>
      </template>
      <div
        v-if="!readonly"
        class="search-tag-container"
        @click.stop=""
        :style="searchGroupPositionStyle"
      >
        <tag-search
          :type="type"
          class="search-container"
          @itemClick="selectTag"
          ref="tagSearch"
          :tags="tags"
          :current="image"
        ></tag-search>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "../../../assets/style/theme.less";

.tag-group-component {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 300;

  .tag-image-container {
    position: relative;

    &.readonly {
      background: rgba(0, 0, 0, 0.1);
    }

    .search-tag-container {
      position: absolute;

      .person-container {
        width: 30px;
        height: 30px;
        background-size: contain;
        border-radius: 50%;
        background-image: url("../../../../public/images/schedule_posts/tag_people/tagpeople_icon@3x.png");
        position: absolute;
        left: -15px;
        top: -15px;
      }
    }
  }
}
</style>

<script>
import tagItem from "./TagItem";
import TagSearch from "./Search";
import { imageLoad, getImageCropPercentage } from "../../../utils/media";
import { CURRENT_PUBLISHER_SIMPLE } from "../../../store/mutation-types";
import { mapGetters } from "vuex";
import { permissionValidate } from "../../../service/publisher";

export default {
  name: "TagGroupComponent",
  components: { tagItem, TagSearch },
  props: {
    image: {
      type: Object, // 默認裁切比例
      required: true,
    },
    tags: {
      // 全部选中tag
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    type: {
      // tag_product 和 tag_people公用了一个控件 people 为tag people product为tag_pruduct
      type: String,
      default: "people",
    },
  },
  data() {
    return {
      searchGroupPosition: { x: 0, y: 0 },
      searchText: "",
      searchList: [],
      loading: false,
      imageLoad: false,
    };
  },
  computed: {
    ...mapGetters({
      publisher: CURRENT_PUBLISHER_SIMPLE,
    }),
    /**
     * 获取当前登录人权限
     */
    permission() {
      return this.publisher.permissions;
    },
    /**
     * tag product权限
     */
    tagProductPermission() {
      return this.permission.product_tagging;
    },
    /**
     * 计算图片裁切比例
     */
    ratio() {
      if (this.readonly) {
        return 1;
      } else {
        return getImageCropPercentage({ model: this.image.crop });
      }
    },
    /**
     * 計算裁切容器大小
     */
    containerStyle() {
      if (this.ratio > 1) {
        return {
          width: `100%`,
          height: `${(1 / this.ratio) * 100}%`,
        };
      } else {
        return {
          height: `100%`,
          width: `${this.ratio * 100}%`,
        };
      }
    },

    list() {
      if (this.searchText) {
        return this.searchList;
      } else {
        // 如果不存在查询条件则显示现在选中项
        return this.tags.filter((u) => u.image);
      }
    },
    /**
     * 计算弹出查询框位置
     */
    searchGroupPositionStyle() {
      return {
        left: `${this.searchGroupPosition.x}px`,
        top: `${this.searchGroupPosition.y}px`,
      };
    },
  },
  watch: {
    searchText(val, old) {
      if (val) {
        this.loading = true;
      } else {
        this.loading = false;
      }
    },
  },
  methods: {
    /**
     * 删除对应tag people
     */
    remove(index) {
      if (this.type === "people") {
        this.image.usertags.splice(index, 1);
      }
      if (this.type === "product") {
        this.image.product_tags.splice(index, 1);
      }
    },
    /**
     * 弹出查询框
     * @param e
     * @returns {Promise<void>}
     */
    async searchClick(e) {
      this.$refs.tagSearch.open();
      // 获取鼠标点击位置
      const startX = e.pageX;
      const startY = e.pageY;
      // 获取父容器位置
      const containerX = this.$refs.group.getBoundingClientRect().left;
      const containerY = this.$refs.group.getBoundingClientRect().top;
      // 计算鼠标点击位置相对于父容器位置
      this.searchGroupPosition.x = startX - containerX;
      this.searchGroupPosition.y = startY - containerY;
    },
    /**
     * 弹出搜索单条点击事件
     * @param item 单条点击事件
     */
    async selectTag(item) {
      const allTags = this.tags;
      let maxTagNum = 15;
      // 多图支持15个 单图支持20个
      if (this.$parent.$parent.images.length === 1) {
        maxTagNum = 20;
      } else {
        maxTagNum = 15;
      }
      // 判断是否可能user tag 已经达到上限
      if (allTags.length === maxTagNum) {
        // 计算是否为新tag
        const isNewTag =
          allTags.findIndex((u) => u.user_id === item.user_id) === -1;
        if (isNewTag) {
          // 如果为新tag 则弹出超出提示
          this.$refs.tagSearch.close();
          this.$message({
            showClose: true,
            message: `Too many tags. Instagram allows up to ${maxTagNum} people tags in one post.`,
            type: "error",
          });
          return;
        }
      }
      try {
        const { clientHeight, clientWidth } = this.$refs.group;
        // 计算userTag数据
        const tagInfo = {
          ...item,
          x: this.searchGroupPosition.x,
          y: this.searchGroupPosition.y,
          position: [],
        };
        tagInfo.position[0] = tagInfo.x / clientWidth;
        tagInfo.position[1] = tagInfo.y / clientHeight;
        // 判断是否重复，如果重复则更新数据，不重复则添加数据
        let tag = this.image.usertags.find((t) => t.user_id === item.user_id);
        // 判断如果是tag product
        if (this.type === "product") {
          tag = this.image.product_tags.find((t) => t.id === item.id);
        }
        if (tag) {
          tag.x = tagInfo.x;
          tag.y = tagInfo.y;
        } else {
          if (this.type === "people") {
            this.image.usertags.push(tagInfo);
          }
          if (this.type === "product") {
            // 判断是否有权限
            if (!this.tagProductPermission) {
              await permissionValidate({ type: "pro" });
              return;
            }
            this.image.product_tags.push(tagInfo);
          }
        }
        // 关闭弹出框
        this.$refs.tagSearch.close();
      } catch (e) {
        console.error(e);
      }
    },
  },
  async created() {
    this.imageLoad = false;
    // 等待图片加载完成之后才能显示tag 标签
    const image = await imageLoad({ src: this.image.url });
    if (this.image.from === "edit") {
      // 如果是编辑状态则将比例变更为图片比例
      const ratio = image.width / image.height;
      this.image.crop.percentage = ratio;
    }
    // 如果图片处于编辑状态则更新到编辑之前的裁切比例
    this.imageLoad = true;
  },
};
</script>
